import { useCallback, useEffect, useRef, useState } from 'react';
import { countMyPendingTasks } from '../services/api';
import useFormTaskSseBus from './useFormTaskSseBus';

/**
 * useMyTasksPendingCount — RC-227 left-nav badge source for /my-tasks.
 *
 * <p>Counts BOTH form_task assignments AND attestation tasks waiting
 * for the current user. Mirrors the {@link useFormsPendingCount} hook
 * but consumes the unified server-side count (single COUNT(*)-per-source
 * sum in {@code MyTasksService.countMyPending}).</p>
 *
 * <h3>SSE coupling</h3>
 * Subscribes to the same three form-task events as the Forms badge —
 * because attestation changes don't fire over the form-tasks SSE
 * channel today, a follow-up RC will need to either:
 *   (a) extend the form-tasks SSE channel to carry attestation events,
 *   (b) add a sibling /me/attestations stream, OR
 *   (c) consolidate both onto a /me/tasks stream.
 *
 * <p>Until then this badge MAY lag on attestation-only changes (a new
 * attestation assigned but no form-task change will refresh only on
 * next page load). Acceptable for RC-227 because attestations have a
 * much lower churn rate than form_tasks; revisit if operators report
 * stale-badge surprise.</p>
 *
 * <h3>Why a second hook instead of generalising useFormsPendingCount</h3>
 * The two badges have semantically distinct meanings — the Forms badge
 * stays form-only because the Forms nav item itself is form-specific.
 * The My Tasks badge aggregates. A single hook with a switch arg would
 * obscure that the two surfaces have different invariants and would
 * force the caller to remember the right enum value at every site.
 *
 * @returns {{count: number, refresh: () => void}}
 */
export default function useMyTasksPendingCount() {
  const [count, setCount] = useState(0);
  const debounceRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await countMyPendingTasks();
      // apiFetch (services/api.js:310) wraps every 2xx JSON response in
      // {ok, data, etag}. The count lives at res.data.count. Prior versions
      // of this hook read res?.count directly, which is undefined against
      // the envelope shape — falling back to 0 and permanently hiding the
      // badge even when the BE correctly returned {count: 1}. Fix reads
      // res.data.count first, then tolerates two alternate shapes for
      // forward-compat: (a) raw envelope-less {count: N} if apiFetch is
      // ever refactored to return raw JSON, and (b) a bare number.
      const raw = res?.data?.count ?? res?.count ?? res;
      const n = typeof raw === 'number' ? raw : 0;
      setCount(Number.isFinite(n) && n >= 0 ? n : 0);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[useMyTasksPendingCount] refresh failed:', err?.message || err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const scheduleRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      refresh();
    }, 150);
  }, [refresh]);

  // Same three subscriptions as useFormsPendingCount — bus is refcounted
  // so the layout shell still only opens one EventSource total even
  // though both badges + the toast notifier + (when active) the
  // FormsToDoPage all subscribe.
  useFormTaskSseBus('form_task_assigned',       scheduleRefresh);
  useFormTaskSseBus('form_task_status_changed', scheduleRefresh);
  useFormTaskSseBus('form_task_reassigned',     scheduleRefresh);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return { count, refresh };
}
